import {
  ConversationListItemResponse,
  ConversationRow,
  isGroupConversation,
  isPublicConversation,
  participantNames,
  renderRole,
} from '@vgrubs/components';

/**
 * The line under the name when there is nothing to preview yet.
 *
 * Each type has a different thing worth knowing about it before you open it:
 * a support thread is a person and the locations they work at, a group of
 * either kind is its members, a direct message is just the person.
 */
const subtitleFor = (thread: ConversationListItemResponse) => {
  if (isGroupConversation(thread.type)) {
    return thread.participants || `${thread.participantCount} members`;
  }
  if (thread.type === 'support') {
    return thread.locations || renderRole(thread.supportUserRole) || '';
  }
  if (thread.dispatch) {
    return [
      thread.dispatch.brandName,
      thread.dispatch.orderId && `#${thread.dispatch.orderId}`,
    ]
      .filter(Boolean)
      .join(' - ');
  }
  return '';
};

/**
 * Who wrote the last line of a dispatcher thread, as a cue in front of it:
 * the customer's own words go bare, ours say whether the AI or the team.
 */
const publicPreview = (
  thread: ConversationListItemResponse,
  preview: string
) =>
  thread.lastMessageSide === 'internal'
    ? `${thread.lastMessageIsAssistant ? 'AI' : 'vGrubs'}: ${preview}`
    : preview;

/**
 * One row of the admin inbox. The row itself is the shared `ConversationRow`
 * (the merchant's Groups tab wears it too); what is admin's is what it says.
 */
export const ChatInboxRow = ({
  thread,
  selected,
  onClick,
}: {
  thread: ConversationListItemResponse;
  selected?: boolean;
  onClick: () => void;
}) => {
  /*
   * An internal last message reads better with a "vGrubs:" cue in a support
   * thread, where admin and support are one participant to the merchant. In a
   * group or a direct message the prefix would say nothing - everyone is
   * internal, or (in a merchant group) every name is real anyway.
   */
  const preview =
    thread.lastMessagePreview || subtitleFor(thread) || 'No messages yet';

  return (
    <ConversationRow
      id={thread.id}
      name={thread.name}
      imageUrl={thread.imageUrl}
      type={thread.type}
      lastMessageAt={thread.lastMessageAt}
      preview={
        thread.type === 'support' && thread.lastMessageSide === 'internal'
          ? `vGrubs: ${preview}`
          : isPublicConversation(thread.type) && thread.lastMessagePreview
          ? publicPreview(thread, preview)
          : preview
      }
      unread={thread.unread}
      hasMention={thread.hasMention}
      mutedUntil={thread.mutedUntil}
      memberNames={
        isGroupConversation(thread.type)
          ? participantNames(thread.participants)
          : undefined
      }
      waiting={!!thread.unansweredSince}
      /* In a public thread the AI has always answered; what can be waiting
         is the person it handed the customer to. */
      waitingLabel={
        isPublicConversation(thread.type) ? 'Needs a person' : undefined
      }
      selected={selected}
      onClick={onClick}
    />
  );
};
